import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { BarChart3, Grid3X3, Link2, LineChart, GitCompareArrows, Sparkles } from "lucide-react";
import Topbar from "../components/Topbar";
import { useAuth } from "../context/AuthContext";
import { useAiReportAvailability } from "../context/AiReportAvailabilityContext";
import { adminMenu, userMenu, NavItem } from "../nav/config";

type Tile = { label: string; to: string; Icon?: React.ComponentType<{ size?: number }> };
type Section = "objects" | "analytics" | "profile" | "admin-manage" | "admin-objects" | "admin-account" | null;

type Groups = {
  main: { key: Section; label: string; desc: string }[];
  children: {
    "admin-manage"?: Tile[];
    "admin-objects"?: Tile[];
    "admin-account"?: Tile[];
    objects?: Tile[];
    analytics?: Tile[];
    profile?: Tile[];
    [key: string]: Tile[] | undefined;
  };
};

export default function IndexPage() {
  const { user, logout } = useAuth();
  const [openSection, setOpenSection] = useState<Section>(null);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (isAdmin) {
      setOpenSection("admin-objects");
    } else {
      setOpenSection("analytics");
    }
  }, [isAdmin]);

  const menu = useMemo(() => {
    if (!user) return userMenu(() => {});
    return user.role === "admin" ? adminMenu(logout) : userMenu(logout);
  }, [user, logout]);

  function tilesByRole(menu: NavItem[], role: "admin" | "user") {
    if (role === "admin") {
      const m = {
        companies: menu.find(i => i.label === "Компании"),
        users: menu.find(i => i.label === "Пользователи"),
        objects: menu.find(i => i.label === "Объекты размещения"),
        competitors: menu.find(i => i.label === "Конкуренты"),
        profile: menu.find(i => i.label === "Профиль"),
      };

      return {
        main: [
          { key: "admin-manage" as Section, label: "Управление", desc: "Компании и пользователи" },
          { key: "admin-objects" as Section, label: "Объекты", desc: "Объекты и конкуренты" },
          { key: "admin-account" as Section, label: "Аккаунт", desc: "Профиль администратора" },
        ],
        children: {
          "admin-manage": [
            ...(m.companies?.to ? [{ label: "Компании", to: m.companies.to, Icon: m.companies.icon }] : []),
            ...(m.users?.to ? [{ label: "Пользователи", to: m.users.to, Icon: m.users.icon }] : []),
          ],
          "admin-objects": [
            ...(m.objects?.to ? [{ label: "Объекты размещения", to: m.objects.to, Icon: m.objects.icon }] : []),
            ...(m.competitors?.to ? [{ label: "Конкуренты", to: m.competitors.to, Icon: m.competitors.icon }] : []),
            { label: "Маппинг", to: "/mapping", Icon: Link2 },
          ],
          "admin-account": [
            ...(m.profile?.to ? [{ label: "Профиль", to: m.profile.to, Icon: m.profile.icon }] : []),
          ]
        }
      };
    }

    const myObjects = menu.find(i => i.label === "Мои объекты размещения");
    const analytics = menu.find(i => i.label === "Аналитика");
    const notifications = menu.find(i => i.label === "Уведомления");
    const profile = menu.find(i => i.label === "Профиль");

    return {
      main: [
        { key: "objects" as Section, label: "Мои объекты", desc: "Управление объектами" },
        { key: "analytics" as Section, label: "Аналитика", desc: "Отчёты и дашборды" },
        { key: "profile" as Section, label: "Профиль и уведомления", desc: "Настройки" },
      ],
      children: {
        objects: myObjects?.to ? [{ label: myObjects.label, to: myObjects.to, Icon: myObjects.icon }] : [],
        analytics: (analytics?.children || []).filter(c => c.to).map(c => ({
          label: c.label,
          to: c.to!,
          Icon: c.icon
        })),
        profile: [
          ...(notifications?.to ? [{ label: notifications.label, to: notifications.to, Icon: notifications.icon }] : []),
          ...(profile?.to ? [{ label: profile.label, to: profile.to, Icon: profile.icon }] : []),
        ]
      }
    };
  }

  const groups: Groups = tilesByRole(menu, isAdmin ? "admin" : "user");

  const toggleSection = (s: Section) => {
    setOpenSection(prev => (prev === s ? null : s));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 text-slate-900">
      <Topbar />

      <main
        className={`mx-auto max-w-[1400px] px-6 py-10 transition-all duration-700
          ${openSection ? "" : "flex flex-col items-center justify-center h-[75vh] animate-softBlur animate-slideToCenter"}
        `}
      >
        <h1 className="text-3xl font-bold mb-10 bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600 text-center">
          Добро пожаловать{user ? `, ${user.fullname ?? user.email}` : ""}
        </h1>

        {/* Нет юзера */}
        {!user && (
          <section className="w-full max-w-3xl mx-auto mb-10">
            <div className="rounded-3xl border border-slate-200 bg-white px-10 py-8
                            shadow-[0_4px_18px_rgba(0,0,0,0.06)] flex flex-col md:flex-row
                            items-center justify-between gap-4">
              <div className="text-center md:text-left">
                <h2 className="text-xl font-semibold text-slate-900">Начните с авторизации</h2>
                <p className="mt-1 text-slate-500 text-sm">
                  Доступ к объектам, аналитике и конкурентам откроется после входа
                </p>
              </div>
              <Link
                to="/auth/login"
                className="inline-flex items-center justify-center rounded-xl
                           bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3
                           text-sm font-semibold text-white shadow-lg hover:brightness-105
                           transition"
              >
                Войти
              </Link>
            </div>
          </section>
        )}

        {/* главные плитки */}
        <div
          className={`grid md:grid-cols-3 w-full transition-all duration-700
            ${openSection ? "gap-8" : "animate-gapGrow animate-softFade"}
          `}
        >
          {groups.main.map(m => (
            <MainTile
              key={m.key}
              label={m.label}
              desc={m.desc}
              isOpen={openSection === m.key}
              onClick={() => toggleSection(m.key as Section)}
            />
          ))}
        </div>

        {/* раскрывающиеся секции */}
        <div className="mt-10 space-y-6 w-full">
          {/* USER analytics */}
          {!isAdmin && openSection === "analytics" && (
            <AnalyticsSection analyticsChildren={groups.children.analytics ?? []} />
          )}

          {/* USER objects */}
          {!isAdmin && openSection === "objects" && (
            <ExpandableSection>
              {(groups.children.objects ?? []).length ? (
                <LargeCard
                  label={(groups.children.objects ?? [])[0].label}
                  to={(groups.children.objects ?? [])[0].to}
                  Icon={(groups.children.objects ?? [])[0].Icon}
                  description="Управление и редактирование объектов"
                />
              ) : (
                <EmptyPlaceholder />
              )}
            </ExpandableSection>
          )}

          {/* USER profile */}
          {!isAdmin && openSection === "profile" && (
            <ExpandableSection>
              <div className="grid gap-6 md:grid-cols-2">
                {(groups.children.profile ?? []).map((p, i) => (
                  <LargeCard
                    key={i}
                    label={p.label}
                    to={p.to}
                    Icon={p.Icon}
                    description="Просмотр и изменение данных профиля"
                  />
                ))}
              </div>
            </ExpandableSection>
          )}

          {/* ADMIN manage */}
          {isAdmin && openSection === "admin-manage" && (
            <ExpandableSection>
              <div className="grid gap-6 md:grid-cols-2">
                {(groups.children["admin-manage"] ?? []).map((c, i) => (
                  <LargeCard key={i} label={c.label} to={c.to} Icon={c.Icon} description="Административный раздел" />
                ))}
              </div>
            </ExpandableSection>
          )}

          {/* ADMIN objects */}
          {isAdmin && openSection === "admin-objects" && (
            <ExpandableSection>
              <div className="grid gap-6 md:grid-cols-2">
                {(groups.children["admin-objects"] ?? []).map((c, i) => (
                  <LargeCard key={i} label={c.label} to={c.to} Icon={c.Icon} description="Управление объектами" />
                ))}
              </div>
            </ExpandableSection>
          )}

          {/* ADMIN account */}
          {isAdmin && openSection === "admin-account" && (
            <ExpandableSection>
              <div className="grid gap-6 md:grid-cols-1">
                {(groups.children["admin-account"] ?? []).map((c, i) => (
                  <LargeCard key={i} label={c.label} to={c.to} Icon={c.Icon} description="Настройки администратора" />
                ))}
              </div>
            </ExpandableSection>
          )}
        </div>
      </main>
    </div>
  );
}

/* Компоненты  */

function MainTile({
  label,
  desc,
  isOpen,
  onClick,
}: {
  label: string;
  desc: string;
  isOpen: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`group relative overflow-hidden rounded-3xl border w-full h-48 flex flex-col items-center justify-center text-center px-6 transition-all duration-300 animate-softFade
        ${
          isOpen
            ? "border-indigo-400 bg-gradient-to-tr from-indigo-50 to-purple-50 shadow-[0_8px_24px_rgba(99,102,241,0.15)] scale-[1.03]"
            : "border-slate-200 bg-white hover:shadow-[0_6px_20px_rgba(0,0,0,0.06)] hover:-translate-y-1"
        }`}
    >
      <div
        className={`absolute inset-0 pointer-events-none bg-gradient-to-tr from-indigo-100 to-transparent transition-opacity ${
          isOpen ? "opacity-70" : "opacity-0 group-hover:opacity-100"
        }`}
      />
      {!isOpen && (
        <div className="absolute inset-x-6 bottom-4 h-10 rounded-full bg-gradient-to-r from-indigo-200/60 to-purple-200/60 blur-2xl opacity-0 transition duration-300 group-hover:opacity-80" />
      )}
      <div className="relative z-10">
        <h2 className="text-xl font-semibold mb-1 text-slate-800">{label}</h2>
        <p className="text-slate-500 text-sm">{desc}</p>
      </div>
    </button>
  );
}

/* Аналитика раздел */

function AnalyticsSection({ analyticsChildren }: { analyticsChildren: NavItem[] }) {
  const hasAiReports = useAiReportAvailability();
  const dashboard = analyticsChildren.find((item) => item.to === "/analytics/dashboard");
  const dateDelta = analyticsChildren.find((item) => item.to === "/analytics/date-delta");
  const trendsCompetitors = analyticsChildren.find((item) => item.to === "/analytics/trends/competitors");

  return (
    <div className="animate-slideDown rounded-3xl border border-slate-200 bg-slate-50/80 backdrop-blur-sm p-10 shadow-[0_4px_24px_rgba(0,0,0,0.05)] space-y-10">
      {hasAiReports && <Link
        to="/ai-report"
        className="group/ai-card relative block overflow-hidden rounded-3xl border border-indigo-200/70 bg-white px-6 py-5 shadow-[0_0_32px_rgba(99,102,241,0.22)] transition-all duration-300 hover:-translate-y-1 hover:border-indigo-300 hover:shadow-[0_0_42px_rgba(99,102,241,0.34)]"
      >
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-indigo-100/80 via-purple-100/60 to-cyan-100/70 transition-opacity duration-300 group-hover/ai-card:opacity-90" />
        <div className="pointer-events-none absolute -right-8 -top-10 h-28 w-28 rounded-full bg-cyan-300/30 blur-2xl transition-transform duration-500 group-hover/ai-card:translate-x-2 group-hover/ai-card:translate-y-2" />
        <div className="pointer-events-none absolute -bottom-12 left-10 h-28 w-36 rounded-full bg-indigo-400/20 blur-2xl transition-transform duration-500 group-hover/ai-card:-translate-x-2 group-hover/ai-card:-translate-y-1" />
        <div className="relative flex items-center gap-3">
          <span className="inline-grid h-12 w-12 place-items-center rounded-2xl bg-white text-indigo-600 shadow-[0_0_22px_rgba(99,102,241,0.28)] transition-transform duration-300 group-hover/ai-card:rotate-6 group-hover/ai-card:scale-105">
            <Sparkles size={24} />
          </span>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-500">ИИ-отчёт</p>
            <h3 className="mt-1 text-lg font-semibold text-slate-900">Помощник по аналитике</h3>
          </div>
        </div>
      </Link>}

      {/* Главная панель */}
      <div>
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Главная панель</h3>
        <div className="grid gap-6 md:grid-cols-2">
          <LargeCard
            label={dashboard?.label ?? "Дашборд"}
            to={dashboard?.to ?? "/analytics/dashboard"}
            Icon={dashboard?.icon ?? BarChart3}
            description="Общая статистика по объектам и показателям"
          />
          <LargeCard
            label={dateDelta?.label ?? "Календарь цен"}
            to={dateDelta?.to ?? "/analytics/date-delta"}
            Icon={dateDelta?.icon ?? GitCompareArrows}
            description="Сравнение минимальных цен с предыдущей датой парсинга"
          />
        </div>
      </div>

      {/* Сравнения / таблицы */}
      <div>
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Сравнения / таблицы</h3>
        <div className="grid gap-6 md:grid-cols-2">
          <LargeCard
            label="Паритет цен"
            to="/analytics/platforms"
            Icon={Grid3X3}
            description="Сравнение показателей между платформами"
          />
          <LargeCard
            label={trendsCompetitors?.label ?? "Тренды по конкурентам"}
            to={trendsCompetitors?.to ?? "/analytics/trends/competitors"}
            Icon={trendsCompetitors?.icon ?? LineChart}
            description="Рост и спад позиций конкурентов"
          />
        </div>
      </div>
    </div>
  );
}

/* Карточки */

function ExpandableSection({ children }: { children: React.ReactNode }) {
  return (
    <div className="animate-slideDown overflow-hidden border border-slate-200 rounded-2xl bg-slate-50 p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
      {children}
    </div>
  );
}

function LargeCard({
  label,
  to,
  Icon,
  description,
}: Tile & { description?: string }) {
  const baseClassName = `group relative flex flex-col items-start justify-between rounded-3xl border p-8 min-h-[180px]
                 shadow-[0_2px_16px_rgba(0,0,0,0.04)] transition-all duration-300`;

  return (
    <Link
      to={to}
      className={`${baseClassName} border-slate-200 bg-white hover:shadow-[0_6px_30px_rgba(0,0,0,0.08)] hover:-translate-y-1`}
    >
      <div className="flex items-center gap-3 mb-4">
        <span className="inline-grid place-items-center w-12 h-12 rounded-2xl bg-slate-100 text-indigo-500">
          {Icon ? <Icon size={26} /> : null}
        </span>
        <h3 className="text-lg font-semibold text-slate-800">{label}</h3>
      </div>
      <p className="text-sm text-slate-500">{description}</p>
    </Link>
  );
}

function EmptyPlaceholder() {
  return (
    <div className="text-slate-500 text-center py-10 border border-dashed border-slate-300 rounded-2xl bg-white/60">
      Нет доступных разделов
    </div>
  );
}
